import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useQueryClient } from '@tanstack/react-query';
import {
  NUTRITION_RATE_LOCKED_AMBIGUOUS_STEPS_MESSAGE,
  NUTRITION_RATE_LOCKED_OPEN_PLAN_MESSAGE,
  NUTRITION_RATE_LOCKED_UNSAVED_MESSAGE,
  TASK_SAVE_ERROR_MESSAGE,
} from 'constants/messageConstants';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import {
  NUTRITION_ADD_FAILED_SAVE_DESCRIPTION,
  NUTRITION_FAILED_SAVE_DESCRIPTION,
  NUTRITION_NEW_APPLICATION_LABEL,
  NUTRITION_REMOVE_FAILED_SAVE_DESCRIPTION,
  NUTRITION_TASK_NO_PRODUCTS_LABEL,
  NUTRITION_UNDO_ADD_LABEL,
  NUTRITION_UNDO_HISTORY_LIMIT,
  NUTRITION_UNDO_RATE_LABEL,
  NUTRITION_UNDO_REMOVE_LABEL,
} from 'constants/uiConstants';
import { useDeleteInputTask } from 'hooks/inputs/useDeleteInputTask';
import { useUpsertInputTask } from 'hooks/inputs/useUpsertInputTask';
import { useFetchUserProfile } from 'hooks/useFetchUserProfile';
import type { IInput, IItem, IUnit } from 'interfaces/IInput';
import { type IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import { hasDuplicateStepIds } from 'pages/FarmsPage/Workspace/Tasks/hasDuplicateStepIds';
import {
  buildStepFromSelection,
  type StepSelection,
} from 'pages/FarmsPage/Workspace/Tasks/stepFactory';
import { buildTask } from 'pages/FarmsPage/Workspace/Tasks/taskFactory';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';
import { toast } from 'utils/toast';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import { v4 as uuidv4 } from 'uuid';

import { getActiveOrganisationId } from '../../../../../redux/selectors/ActiveOrganisationSelectors';
import { getSelectedInputId } from '../../../../../redux/selectors/InputSelectors';
import {
  nutritionSaveFailed,
  nutritionSaveStarted,
  nutritionSaveSucceeded,
} from '../../../../../redux/slices/InsightsSlice';
import type { RootState } from '../../../../../redux/Store';

import type { NutritionProductLine } from './computeNutritionAudit';
import {
  listTaskTargets,
  NEW_APPLICATION_TARGET,
  type NutritionIntent,
  nutritionTaskKey,
  type NutritionTaskOp,
  type NutritionTaskTarget,
  resolveNutritionIntent,
} from './nutritionIntents';
import type { TipText } from './TipContent';

/** One change to send: its intents go in order, one at a time. */
export interface NutritionChange {
  /** Failed saves are kept per key, latest wins. */
  key: string;
  /** For the save status if it fails, e.g. "Urea on North paddock, 2025". */
  description: string;
  intents: NutritionIntent[];
}

/** A product from the picker, at the rate, unit and price the advisor chose. */
export interface NutritionProductChoice {
  selection: StepSelection;
  rate: number;
  /** Rate unit; the catalogue's when not changed. */
  unit: IUnit | null;
  /** Price per unit; the catalogue's when not changed. */
  cost: number | null;
}

/** When and at what timing a new application goes on. */
export interface NutritionNewApplication {
  endDate: string | null;
  endDateHasDay: boolean;
  timing: IItem | null;
}

/** Products to add to one plan, together on one application. */
export interface NutritionAddProduct {
  inputId: string;
  /** "North paddock, 2025" — for messages. */
  location: string;
  /** A taskId on the plan, or NEW_APPLICATION_TARGET. */
  target: string;
  products: NutritionProductChoice[];
  /** Date and timing for a new application; ignored for an existing one. */
  newApplication?: NutritionNewApplication;
}

export interface NutritionProgramEdit {
  /** False in Actual mode — Actual values are entered elsewhere, never here. */
  canEdit: boolean;
  /** Why this line can't be changed here, or null when it can. */
  lockReason: (line: NutritionProductLine) => TipText | null;
  /** Why nothing can be added to this plan from here, or null when it can. */
  planLockReason: (inputId: string) => TipText | null;
  /** True while a save for this line's task is in flight. */
  isSaving: (line: NutritionProductLine) => boolean;
  /** True while any save for this plan is in flight. */
  isPlanSaving: (inputId: string) => boolean;
  /** The rate to show: the one just typed while its save is in flight, else the plan's. */
  displayRate: (line: NutritionProductLine) => number;
  commitRate: (line: NutritionProductLine, rate: number) => void;
  /** Where a product on this plan can go: its applications and sowings, then a new application. */
  taskTargets: (
    inputId: string,
    excludeTaskId?: string
  ) => NutritionTaskTarget[];
  addProduct: (product: NutritionAddProduct) => boolean;
  /** Removes only this product; its application goes too if nothing else is on it. */
  removeProduct: (line: NutritionProductLine) => boolean;
  /** What Undo would put back, e.g. "Undo add Urea", or null with nothing to undo. */
  undoLabel: string | null;
  /** False when there's nothing to undo, or its saves haven't settled yet. */
  undo: () => boolean;
}

interface UndoEntry {
  label: string;
  changes: NutritionChange[];
}

interface RunResult {
  sent: boolean;
  ok: boolean;
  /** Intents that put back what was saved, in the order to send them. */
  inverses: NutritionIntent[];
}

const NOT_SENT: RunResult = { sent: false, ok: false, inverses: [] };

/** "Urea" or "Urea, MAP" — for messages and undo labels. */
const productNames = (products: NutritionProductChoice[]) =>
  products.map((p) => p.selection.item).join(', ');

/** How many plans an undo saves at once. */
const UNDO_CONCURRENCY = 4;

const taskKeysOf = (intents: NutritionIntent[]) =>
  Array.from(
    new Set(intents.map((i) => nutritionTaskKey(i.inputId, i.taskId)))
  );

/**
 * Setting the fertiliser program from the Nutrition lens (OP-2508 rates,
 * OP-2523 add / remove and undo).
 *
 * Every change is a list of intents (nutritionIntents.ts), each sent as one
 * whole-task save or delete built from the latest cached plan — the same
 * shapes the Tasks grid sends — one after another.
 *
 * Each change reports to the workspace's save status label (Saving…, Saved,
 * or an error with Retry) via insightsReducer.nutritionSave. What didn't
 * save is kept there as intents until a retry succeeds; Retry in the label
 * bumps retryRequest, and this hook resends them.
 *
 * Undo keeps the last few changes made here (this lens visit only). Undoing
 * sends the inverse intents, so it puts back what was there, not a stale
 * copy of the task.
 *
 * A plan is locked here while it's open in Planning or has unsaved edits
 * there: the Planning draft doesn't pick up saves made elsewhere, so its next
 * task save would put the old values back. A task is also locked while its
 * own save is in flight (a second save would be built without the first) and
 * when its step ids are ambiguous.
 */
export const useNutritionProgramEdit = (
  inputs: IInput[],
  canEdit: boolean
): NutritionProgramEdit => {
  const activeOrganisationId = useSelector(getActiveOrganisationId);
  const selectedInputId = useSelector(getSelectedInputId);
  const { failed, retryRequest } = useSelector(
    (state: RootState) => state.insightsReducer.nutritionSave
  );
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data: userProfile } = useFetchUserProfile();
  const { mutateAsync: upsertTask } = useUpsertInputTask({
    activeOrganisationId,
  });
  const { mutateAsync: deleteTask } = useDeleteInputTask({
    activeOrganisationId,
  });

  // Tasks with a save in flight. The ref is checked and set synchronously,
  // so two changes started in the same tick can't both claim a task; the
  // state copy re-renders the fields.
  const busyRef = useRef<Set<string>>(new Set());
  const [busyTaskKeys, setBusyTaskKeys] = useState<Set<string>>(
    () => new Set()
  );
  const setBusy = useCallback((keys: string[], busy: boolean) => {
    keys.forEach((key) =>
      busy ? busyRef.current.add(key) : busyRef.current.delete(key)
    );
    setBusyTaskKeys(new Set(busyRef.current));
  }, []);

  // The rate just typed, per product line, held from the moment the field is
  // left until its save settles — so the box never shows the old value in
  // between, however long the cache takes to catch up.
  const [pendingRates, setPendingRates] = useState<Map<string, number>>(
    () => new Map()
  );
  const setPendingRate = useCallback((key: string, rate: number | null) => {
    setPendingRates((prev) => {
      const next = new Map(prev);
      if (rate == null) next.delete(key);
      else next.set(key, rate);
      return next;
    });
  }, []);

  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  // Another org's plans aren't in this cache, so its undo history is meaningless here.
  useEffect(() => setUndoStack([]), [activeOrganisationId]);
  const pushUndo = useCallback((entry: UndoEntry) => {
    if (entry.changes.length === 0) return;
    setUndoStack((prev) =>
      [...prev, entry].slice(-NUTRITION_UNDO_HISTORY_LIMIT)
    );
  }, []);

  /** The plans as cached right now — fresher than the inputs prop mid-change. */
  const latestInputs = useCallback(
    (): IInput[] =>
      queryClient.getQueryData<IInput[]>([
        INPUTS_QUERY_KEY,
        activeOrganisationId,
      ]) ?? inputs,
    [activeOrganisationId, inputs, queryClient]
  );

  const findInput = useCallback(
    (inputId: string) => latestInputs().find((input) => input.id === inputId),
    [latestInputs]
  );

  const findTask = useCallback(
    (inputId: string, taskId: string): IInputTask | undefined =>
      findInput(inputId)?.tasks?.find((task) => task.taskId === taskId),
    [findInput]
  );

  const planLockReason = useCallback(
    (inputId: string): TipText | null => {
      if (inputId === selectedInputId) {
        return NUTRITION_RATE_LOCKED_OPEN_PLAN_MESSAGE;
      }
      if (BrowserStorageUtils.hasEditedInput(inputId)) {
        return NUTRITION_RATE_LOCKED_UNSAVED_MESSAGE;
      }
      return null;
    },
    [selectedInputId]
  );

  const taskLockReason = useCallback(
    (inputId: string, taskId: string): TipText | null => {
      const planReason = planLockReason(inputId);
      if (planReason) return planReason;
      const task = findTask(inputId, taskId);
      if (task && hasDuplicateStepIds(task.steps)) {
        return NUTRITION_RATE_LOCKED_AMBIGUOUS_STEPS_MESSAGE;
      }
      return null;
    },
    [findTask, planLockReason]
  );

  const sendOp = useCallback(
    (op: NutritionTaskOp) =>
      op.kind === 'upsert'
        ? upsertTask({ inputId: op.inputId, task: op.task })
        : deleteTask({ inputId: op.inputId, taskId: op.taskId }),
    [deleteTask, upsertTask]
  );

  /**
   * Sends one change, intent by intent. Not sent (sent: false) when one of
   * its tasks is locked or already saving. If an intent fails, it and the
   * ones after it are kept for Retry; the ones before it stay saved.
   */
  const runChange = useCallback(
    async (change: NutritionChange): Promise<RunResult> => {
      const taskKeys = taskKeysOf(change.intents);
      const blocked =
        change.intents.some((i) => taskLockReason(i.inputId, i.taskId)) ||
        taskKeys.some((key) => busyRef.current.has(key));
      if (blocked) return NOT_SENT;

      setBusy(taskKeys, true);
      dispatch(nutritionSaveStarted());
      const inverses: NutritionIntent[] = [];
      try {
        for (let i = 0; i < change.intents.length; i += 1) {
          const { op, inverse } = resolveNutritionIntent(
            latestInputs(),
            change.intents[i]
          );
          if (!op) continue;
          try {
            await sendOp(op);
          } catch (error) {
            const reason = getApiErrorMessage(error, TASK_SAVE_ERROR_MESSAGE);
            dispatch(
              nutritionSaveFailed({
                key: change.key,
                description: change.description,
                intents: change.intents.slice(i),
                reason,
              })
            );
            toast.error(reason);
            return { sent: true, ok: false, inverses };
          }
          inverses.unshift(...inverse);
        }
        dispatch(nutritionSaveSucceeded(change.key));
        return { sent: true, ok: true, inverses };
      } finally {
        setBusy(taskKeys, false);
      }
    },
    [dispatch, latestInputs, sendOp, setBusy, taskLockReason]
  );

  /** Sends changes (one per plan) a few at a time — undo of a change across plans; returns each one's result in order. */
  const runChanges = useCallback(
    async (changes: NutritionChange[]): Promise<RunResult[]> => {
      const results: RunResult[] = new Array(changes.length);
      let next = 0;
      const worker = async () => {
        while (next < changes.length) {
          const index = next;
          next += 1;
          results[index] = await runChange(changes[index]);
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(UNDO_CONCURRENCY, changes.length) },
          worker
        )
      );
      return results;
    },
    [runChange]
  );

  /** Sends one change made by the advisor and records it for undo; null when it can't be sent. */
  const runSingle = useCallback(
    (change: NutritionChange, undoLabel: string): Promise<RunResult> | null => {
      const taskKeys = taskKeysOf(change.intents);
      if (
        !canEdit ||
        change.intents.length === 0 ||
        change.intents.some((i) => taskLockReason(i.inputId, i.taskId)) ||
        taskKeys.some((key) => busyRef.current.has(key))
      ) {
        return null;
      }
      return runChange(change).then((result) => {
        if (result.ok) {
          pushUndo({
            label: undoLabel,
            changes: [{ ...change, key: uuidv4(), intents: result.inverses }],
          });
        }
        return result;
      });
    },
    [canEdit, pushUndo, runChange, taskLockReason]
  );

  const commitRate = useCallback(
    (line: NutritionProductLine, rate: number) => {
      if (rate < 0 || rate === line.rate) return;
      const sent = runSingle(
        {
          key: line.key,
          description: NUTRITION_FAILED_SAVE_DESCRIPTION(
            line.productName,
            line.location
          ),
          intents: [
            {
              kind: 'setRate',
              inputId: line.inputId,
              taskId: line.taskId,
              stepId: line.stepId,
              rate,
            },
          ],
        },
        NUTRITION_UNDO_RATE_LABEL(line.productName)
      );
      if (!sent) return;
      setPendingRate(line.key, rate);
      sent.finally(() => setPendingRate(line.key, null));
    },
    [runSingle, setPendingRate]
  );

  /** A new planned application on the plan, to put a product on. */
  const newApplication = useCallback(
    (input: IInput): IInputTask =>
      buildTask(input, 'application', InputTaskCategory.Plan, userProfile),
    [userProfile]
  );

  const buildAddChange = useCallback(
    ({
      inputId,
      location,
      target,
      products,
      newApplication: newApplicationFields,
    }: NutritionAddProduct): NutritionChange | null => {
      const input = findInput(inputId);
      if (!input || products.length === 0) return null;
      const isNew = target === NEW_APPLICATION_TARGET;
      const task = isNew
        ? { ...newApplication(input), ...newApplicationFields }
        : findTask(inputId, target);
      if (!task) return null;
      const steps = products.map(({ selection, rate, unit, cost }) =>
        buildStepFromSelection(
          task,
          { ...selection, rate, unit: unit ?? selection.unit, cost },
          userProfile
        )
      );
      return {
        key: uuidv4(),
        description: NUTRITION_ADD_FAILED_SAVE_DESCRIPTION(
          productNames(products),
          location
        ),
        intents: [
          {
            kind: 'addStep',
            inputId,
            taskId: task.taskId,
            steps,
            task: isNew ? task : undefined,
          },
        ],
      };
    },
    [findInput, findTask, newApplication, userProfile]
  );

  const addProduct = useCallback(
    (product: NutritionAddProduct) => {
      const change = buildAddChange(product);
      return (
        !!change &&
        !!runSingle(
          change,
          NUTRITION_UNDO_ADD_LABEL(productNames(product.products))
        )
      );
    },
    [buildAddChange, runSingle]
  );

  const inputsById = useMemo(
    () => new Map(inputs.map((input) => [input.id, input])),
    [inputs]
  );

  const taskTargets = useCallback(
    (inputId: string, excludeTaskId?: string) =>
      listTaskTargets(
        inputsById.get(inputId),
        {
          noProducts: NUTRITION_TASK_NO_PRODUCTS_LABEL,
          newApplication: NUTRITION_NEW_APPLICATION_LABEL,
        },
        excludeTaskId
      ),
    [inputsById]
  );

  const removeProduct = useCallback(
    (line: NutritionProductLine) =>
      !!runSingle(
        {
          key: uuidv4(),
          description: NUTRITION_REMOVE_FAILED_SAVE_DESCRIPTION(
            line.productName,
            line.location
          ),
          intents: [
            {
              kind: 'removeStep',
              inputId: line.inputId,
              taskId: line.taskId,
              stepId: line.stepId,
              deleteIfEmpty: true,
            },
          ],
        },
        NUTRITION_UNDO_REMOVE_LABEL(line.productName)
      ),
    [runSingle]
  );

  const undoEntry = undoStack[undoStack.length - 1] ?? null;

  const undo = useCallback((): boolean => {
    if (!canEdit || !undoEntry) return false;
    const keys = undoEntry.changes.flatMap((c) => taskKeysOf(c.intents));
    // Wait until its saves have settled — undoing mid-save would race them.
    if (keys.some((key) => busyRef.current.has(key))) return false;
    setUndoStack((prev) => prev.slice(0, -1));
    runChanges(undoEntry.changes).then((results) => {
      // A change that couldn't be sent (its plan is now locked) stays undoable.
      const notSent = undoEntry.changes.filter((_, i) => !results[i].sent);
      if (notSent.length > 0) {
        pushUndo({ label: undoEntry.label, changes: notSent });
      }
    });
    return true;
  }, [canEdit, pushUndo, runChanges, undoEntry]);

  // Retry in the save status label: resend failed changes. A change that
  // can't be sent right now (its task is saving, or its plan is open in
  // Planning) stays listed for the next retry.
  const lastRetryRef = useRef(retryRequest);
  useEffect(() => {
    if (retryRequest === lastRetryRef.current) return;
    lastRetryRef.current = retryRequest;
    failed.forEach((item) => {
      runChange({
        key: item.key,
        description: item.description,
        intents: item.intents,
      });
    });
  }, [failed, retryRequest, runChange]);

  const isSaving = useCallback(
    (line: NutritionProductLine) =>
      busyTaskKeys.has(nutritionTaskKey(line.inputId, line.taskId)),
    [busyTaskKeys]
  );

  const isPlanSaving = useCallback(
    (inputId: string) =>
      Array.from(busyTaskKeys).some((key) => key.startsWith(`${inputId}:`)),
    [busyTaskKeys]
  );

  const lockReason = useCallback(
    (line: NutritionProductLine) => taskLockReason(line.inputId, line.taskId),
    [taskLockReason]
  );

  const displayRate = useCallback(
    (line: NutritionProductLine) => pendingRates.get(line.key) ?? line.rate,
    [pendingRates]
  );

  return useMemo(
    () => ({
      canEdit,
      lockReason,
      planLockReason,
      isSaving,
      isPlanSaving,
      displayRate,
      commitRate,
      taskTargets,
      addProduct,
      removeProduct,
      undoLabel: canEdit && undoEntry ? undoEntry.label : null,
      undo,
    }),
    [
      addProduct,
      canEdit,
      commitRate,
      displayRate,
      isPlanSaving,
      isSaving,
      lockReason,
      planLockReason,
      removeProduct,
      taskTargets,
      undo,
      undoEntry,
    ]
  );
};
